import { useLayoutEffect, useRef } from 'react';
import type { CSSProperties, DragEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode, RefObject } from 'react';
import { PlaneSurface } from './PlaneSurface';
import { TABLE } from './tableFit';
import type { TableState } from './useTable';
import './table.css';

/**
 * The canvas as one bounded table on a page. Pressing full canvas lets it grow to the window under
 * the bar; the page keeps its box in the flow, so coming back finds it where it was.
 */
export function Table({
  table,
  surfaceRef,
  className,
  style,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onDoubleClickEmpty,
  onDragOver,
  onDrop,
  overlay,
  tools,
  children,
}: {
  table: TableState;
  surfaceRef: RefObject<HTMLDivElement | null>;
  className?: string;
  style?: CSSProperties;
  onPointerDown?: (event: ReactPointerEvent) => void;
  onPointerMove?: (event: ReactPointerEvent) => void;
  onPointerUp?: (event: ReactPointerEvent) => void;
  onDoubleClickEmpty?: (event: ReactMouseEvent) => void;
  onDragOver?: (event: DragEvent) => void;
  onDrop?: (event: DragEvent) => void;
  /** Drawn on the table, over the canvas and under its controls. */
  overlay?: ReactNode;
  /** First among the corner tools. */
  tools?: ReactNode;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const was = useRef<DOMRect | null>(null);
  const { full, setFull, slot, settle } = table;

  const toggle = () => {
    was.current = box.current?.getBoundingClientRect() ?? null;
    setFull(!full);
  };

  useLayoutEffect(() => {
    const from = was.current;
    was.current = null;
    const el = box.current;
    const home = slot.current;
    if (!from || !el || !home || !el.animate) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const to = el.getBoundingClientRect();
    // Fixed, the box is placed in the page under the head; at rest, in its slot.
    const rel = (full ? el.closest('.nook-main') : home)?.getBoundingClientRect() ?? { left: 0, top: 0 };
    const at = (r: DOMRect) => ({ left: `${r.left - rel.left}px`, top: `${r.top - rel.top}px`, width: `${r.width}px`, height: `${r.height}px` });
    void el.animate([at(from), at(to)], { duration: 180, easing: 'ease' }).finished.then(settle, () => {});
  }, [full, slot, settle]);

  return (
    <div ref={slot} className="table-slot" style={{ height: table.height }}>
      <div ref={box} className={`table${full ? ' is-full' : ''}`}>
        <div className="nook-stage" onDragOver={onDragOver} onDrop={onDrop}>
          <PlaneSurface
            cam={table.cam}
            surfaceRef={surfaceRef}
            className={className}
            style={style}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onDoubleClickEmpty={onDoubleClickEmpty}
            claimsX={table.claimsX}
          >
            {children}
          </PlaneSurface>
        </div>
        {overlay}
        <div className="table-ctl">
          {tools}
          {!full && table.reach.max > 0 && <Reach reach={table.reach} onPan={table.panTo} />}
          <button
            type="button"
            className="table-ctl-btn"
            aria-label={full ? 'Back to the page' : 'Full canvas'}
            title={full ? 'Back to the page (Esc)' : 'Full canvas'}
            onClick={toggle}
          >
            <Arrows inward={full} />
          </button>
          <span className="table-ctl-zoom">
            <button type="button" className="table-ctl-btn" aria-label="Zoom out" onClick={() => table.zoomBy(1 / TABLE.step)}>
              −
            </button>
            <span className="table-ctl-percent">{table.percent}%</span>
            <button type="button" className="table-ctl-btn" aria-label="Zoom in" onClick={() => table.zoomBy(TABLE.step)}>
              +
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

/** How far to go for one press of an arrow, in the table's own pixels. */
const STEP = 280;

/**
 * Where a table wider than its page can be reached with a mouse: a bar in the corner control that shows
 * how much there is and where this is, dragged, pressed on either side of its thumb, or stepped by
 * its arrows. The wheel and a sideways swipe do the same.
 */
function Reach({ reach, onPan }: { reach: { pan: number; max: number; width: number }; onPan: (to: number) => void }) {
  const track = useRef<HTMLSpanElement>(null);
  const total = reach.width + reach.max;
  const thumb = Math.max(32, ((track.current?.offsetWidth ?? reach.width) * reach.width) / total);

  const grab = (event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation();
    const lane = (track.current?.offsetWidth ?? reach.width) - thumb;
    if (lane <= 0) return;
    const start = { x: event.clientX, pan: reach.pan };
    const el = event.currentTarget;
    el.setPointerCapture(event.pointerId);
    const move = (e: PointerEvent) => onPan(start.pan + ((e.clientX - start.x) * reach.max) / lane);
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };

  const lane = (track.current?.offsetWidth ?? reach.width) - thumb;
  const left = reach.max > 0 && lane > 0 ? (reach.pan / reach.max) * lane : 0;
  return (
    <div className="table-reach" onPointerDown={(e) => e.stopPropagation()}>
      <button type="button" className="table-reach-step" aria-label="Scroll left" disabled={reach.pan <= 0} onClick={() => onPan(reach.pan - STEP)}>
        ‹
      </button>
      <span
        ref={track}
        className="table-reach-track"
        role="scrollbar"
        aria-orientation="horizontal"
        aria-label="Across the table"
        aria-valuemin={0}
        aria-valuemax={Math.round(reach.max)}
        aria-valuenow={Math.round(reach.pan)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') onPan(reach.pan - STEP / 2);
          else if (e.key === 'ArrowRight') onPan(reach.pan + STEP / 2);
        }}
        onPointerDown={(e) => {
          if (e.target !== e.currentTarget) return;
          const at = e.clientX - e.currentTarget.getBoundingClientRect().left;
          onPan(at < left ? reach.pan - reach.width * 0.8 : reach.pan + reach.width * 0.8);
        }}
      >
        <span className="table-reach-thumb" style={{ left, width: thumb }} onPointerDown={grab} />
      </span>
      <button type="button" className="table-reach-step" aria-label="Scroll right" disabled={reach.pan >= reach.max} onClick={() => onPan(reach.pan + STEP)}>
        ›
      </button>
    </div>
  );
}

function Arrows({ inward }: { inward: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {inward ? (
        <path d="M8.2 2V5.8H12M8.2 5.8 13 1M5.8 12V8.2H2M5.8 8.2 1 13" />
      ) : (
        <path d="M9 1H13V5M13 1 8.5 5.5M5 13H1V9M1 13 5.5 8.5" />
      )}
    </svg>
  );
}
